import { API_BASE } from '../../lib/apiOrigin';
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { isUSPostalCode, US_STATES } from './usStates';

const API = API_BASE;
const emptyLookup = () => ({ status: 'idle', places: [], version: '', postalCode: '' });
const clearLocation = { city: '', state: '', location_confirmed: false, location_lookup_version: '' };
export const ZIP_LOCATION_ERRORS = [
  'zip_lookup_changed', 'zip_place_mismatch', 'zip_lookup_unavailable',
  'location_confirmation_required', 'zip_not_found', 'zip_invalid',
];

/** Treat a malformed/stale response as unavailable, never as a verified location. */
export function parseZipLookup(data, postalCode) {
  if (data?.country !== 'US' || data?.postal_code !== postalCode ||
      typeof data.lookup_version !== 'string' || !data.lookup_version.trim() ||
      !Array.isArray(data.places) || data.places.length === 0) throw new Error('Invalid ZIP response');
  const places = data.places.map(place => {
    if (typeof place?.city !== 'string' || !place.city.trim() ||
        !Object.prototype.hasOwnProperty.call(US_STATES, place.state_code) ||
        US_STATES[place.state_code] !== place.state) throw new Error('Invalid ZIP place');
    return { city: place.city, state: place.state, state_code: place.state_code };
  });
  return { status: 'ready', places, version: data.lookup_version, postalCode };
}

/** Kept at wizard level so a verified choice survives Back/Continue navigation. */
export function useZipLocation({ form, setForm, active }) {
  const [lookup, setLookup] = useState(emptyLookup);
  const mounted = useRef(false);
  const activeRef = useRef(active);
  const postalRef = useRef(form.postal_code);
  const request = useRef(null);
  const generation = useRef(0);
  activeRef.current = active;
  postalRef.current = form.postal_code;

  const cancel = useCallback(() => {
    generation.current += 1;
    request.current?.abort();
    request.current = null;
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cancel(); };
  }, [cancel]);

  useEffect(() => {
    if (!active) {
      cancel();
      setLookup(previous => previous.status === 'loading' ? emptyLookup() : previous);
    }
  }, [active, cancel]);

  const reset = () => { cancel(); setLookup(emptyLookup()); };
  const rejectLocation = code => {
    cancel();
    setLookup({ ...emptyLookup(), status: code, postalCode: postalRef.current });
    setForm(previous => ({ ...previous, ...clearLocation }));
  };
  const changePostalCode = postalCode => {
    cancel();
    postalRef.current = postalCode;
    setLookup(emptyLookup());
    setForm(previous => ({ ...previous, postal_code: postalCode, ...clearLocation }));
  };

  const search = async () => {
    const postalCode = postalRef.current;
    if (!activeRef.current || !isUSPostalCode(postalCode) || request.current) return;
    cancel();
    const id = generation.current;
    const controller = new AbortController();
    request.current = controller;
    setForm(previous => ({ ...previous, ...clearLocation }));
    setLookup({ ...emptyLookup(), status: 'loading', postalCode });
    const isCurrent = () => mounted.current && activeRef.current &&
      id === generation.current && postalRef.current === postalCode;
    try {
      const response = await axios.get(`${API}/locations/us-zip/${postalCode}`, {
        signal: controller.signal,
        timeout: 10000,
      });
      if (!isCurrent()) return;
      const result = parseZipLookup(response.data, postalCode);
      setLookup(result);
      const place = result.places.length === 1 ? result.places[0] : null;
      setForm(previous => ({
        ...previous,
        city: place?.city || '',
        state: place?.state || '',
        location_confirmed: false,
        location_lookup_version: result.version,
      }));
    } catch (error) {
      if (!isCurrent()) return;
      setLookup({
        ...emptyLookup(), postalCode,
        status: error?.response?.status === 404 ? 'notFound' : 'unavailable',
      });
    } finally {
      if (id === generation.current) request.current = null;
    }
  };

  const selectPlace = value => {
    const place = lookup.places.find((_, index) => String(index) === value);
    setForm(previous => ({
      ...previous,
      city: place?.city || '',
      state: place?.state || '',
      location_confirmed: false,
      location_lookup_version: place ? lookup.version : '',
    }));
  };
  const confirm = checked => {
    const selected = lookup.status === 'ready' && lookup.postalCode === postalRef.current &&
      lookup.places.some(place => place.city === form.city && place.state === form.state);
    setForm(previous => ({ ...previous, location_confirmed: selected && checked === true }));
  };
  return { ...lookup, changePostalCode, search, selectPlace, confirm, reset, rejectLocation };
}
